import { useState } from "react";
import { Scroll, Copy, Check, Download, X, CheckCircle } from "lucide-react";
import { renderMarkdown } from "../utils/markdown";

/**
 * The assembled blueprint — every locked output for a module in one scrollable
 * document. Same scoping rule as OutputPanel: the header is chrome, so it stays
 * in the dark band with the sidebar, and the body is canvas, so it follows the
 * panel-mode toggle.
 */
export function BlueprintPanel({ moduleLabel, outputs, promptNames, onClose, isMobile }) {
  const [copiedAll, setCopiedAll] = useState(false);
  const [copiedSection, setCopiedSection] = useState(null);

  const stripMarkdown = (text) => text.replace(/#{1,3} /gm, "").replace(/\*\*([^*]+)\*\*/g, "$1").replace(/\*([^*]+)\*/g, "$1");

  const handleCopyAll = () => {
    const text = promptNames
      .filter(name => outputs[name])
      .map(name => `${name.toUpperCase()}\n${"─".repeat(name.length)}\n${stripMarkdown(outputs[name])}`)
      .join("\n\n");
    navigator.clipboard.writeText(`${moduleLabel.toUpperCase()} BLUEPRINT\n${"═".repeat(moduleLabel.length + 10)}\n\n${text}`).then(() => {
      setCopiedAll(true);
      setTimeout(() => setCopiedAll(false), 2000);
    });
  };

  const handleDownload = () => {
    const sections = promptNames
      .filter(name => outputs[name])
      .map(name => `${name.toUpperCase()}\n${"─".repeat(Math.max(name.length, 20))}\n\n${stripMarkdown(outputs[name])}`)
      .join("\n\n\n");
    const header = `${moduleLabel.toUpperCase()} — BUSINESS BRILLIANCE BLUEPRINT\n${"═".repeat(50)}\n\n`;
    const blob = new Blob([header + sections], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${moduleLabel.toLowerCase().replace(/\s+/g, "-")}-blueprint.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleCopySection = (name) => {
    const text = `${name.toUpperCase()}\n${"─".repeat(name.length)}\n${stripMarkdown(outputs[name])}`;
    navigator.clipboard.writeText(text).then(() => {
      setCopiedSection(name);
      setTimeout(() => setCopiedSection(null), 2000);
    });
  };

  return (
    <div style={isMobile ? {
      position: "fixed", inset: 0, zIndex: 60, background: "var(--bg-surface)", display: "flex", flexDirection: "column",
    } : {
      width: 380, background: "var(--bg-surface)", borderLeft: "1px solid var(--border-subtle)",
      display: "flex", flexDirection: "column", height: "100vh", flexShrink: 0,
      animation: "slideIn 0.2s ease",
    }}>
      <div style={{ background: "var(--chrome-bg)", padding: "14px 16px", borderBottom: "3px solid var(--chrome-accent)", flexShrink: 0 }}>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 3 }}>
              <Scroll size={13} color="var(--chrome-accent)" />
              <span style={{ color: "var(--chrome-muted)", fontSize: 11, letterSpacing: "0.5px", textTransform: "uppercase" }}>Business Brilliance Blueprint</span>
            </div>
            <h3 style={{ color: "var(--chrome-text)", margin: 0, fontSize: 15, fontWeight: 700 }}>{moduleLabel}</h3>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <button
              onClick={handleCopyAll}
              style={{
                background: copiedAll ? "var(--chrome-accent-quiet)" : "var(--chrome-quiet)", border: "none", borderRadius: 6,
                padding: "5px 10px", cursor: "pointer", display: "flex", alignItems: "center", gap: 5,
                color: copiedAll ? "var(--chrome-accent)" : "var(--chrome-muted)", fontSize: 11, fontWeight: 600, transition: "all 0.15s",
              }}
            >
              {copiedAll ? <Check size={11} /> : <Copy size={11} />}
              {copiedAll ? "Copied!" : "Copy all"}
            </button>
            <button
              onClick={handleDownload}
              title="Download as text file"
              style={{
                background: "var(--chrome-quiet)", border: "none", borderRadius: 6,
                padding: "5px 8px", cursor: "pointer", display: "flex", alignItems: "center",
                color: "var(--chrome-muted)", transition: "color 0.15s",
              }}
            >
              <Download size={13} />
            </button>
            <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", padding: 4 }}>
              <X size={16} color="var(--chrome-muted)" />
            </button>
          </div>
        </div>
      </div>
      <div style={{ flex: 1, overflowY: "auto", padding: "16px", WebkitOverflowScrolling: "touch" }}>
        {promptNames.map((name, i) => {
          const output = outputs[name];
          if (!output) return null;
          const isSectionCopied = copiedSection === name;
          return (
            <div key={i} style={{ marginBottom: 24 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 10 }}>
                <CheckCircle size={13} color="var(--accent)" />
                <h4 style={{ margin: 0, fontSize: 12, fontWeight: 700, color: "var(--text-primary)", textTransform: "uppercase", letterSpacing: "0.5px", flex: 1 }}>{name}</h4>
                <button
                  onClick={() => handleCopySection(name)}
                  style={{
                    background: "none", border: "none", cursor: "pointer", padding: "2px 4px",
                    color: isSectionCopied ? "var(--accent)" : "var(--text-disabled)", display: "flex", alignItems: "center", gap: 3,
                    fontSize: 11, transition: "color 0.15s",
                  }}
                >
                  {isSectionCopied ? <Check size={10} /> : <Copy size={10} />}
                  {isSectionCopied ? "Copied" : "Copy"}
                </button>
              </div>
              <div style={{ fontSize: 13, lineHeight: 1.85, color: "var(--text-primary)", wordBreak: "break-word", borderLeft: "3px solid var(--accent-line)", paddingLeft: 12 }}>
                {renderMarkdown(output)}
              </div>
              {i < promptNames.length - 1 && <div style={{ borderBottom: "1px solid var(--border-panel)", marginTop: 20 }} />}
            </div>
          );
        })}
        {moduleLabel === "Offer Ideation Blueprint" && (
          <div style={{ marginTop: 8, padding: "18px 16px", background: "var(--accent-quiet)", border: "1px solid var(--accent-line)", borderRadius: 12 }}>
            <p style={{ margin: "0 0 6px", fontSize: 13, fontWeight: 700, color: "var(--text-primary)", lineHeight: 1.5 }}>
              Ready to build the whole house?
            </p>
            <p style={{ margin: "0 0 14px", fontSize: 12, color: "var(--text-secondary)", lineHeight: 1.6 }}>
              You just built the foundation. The Accelerator is where we pressure-test it against your full method, ideal client, archetype, and complete offer — and turn it into a business you can sell.
            </p>
            <a
              href="https://info.tevrawood.com/business-brilliance-accelerator"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6,
                width: "100%", padding: "10px 14px", borderRadius: 8,
                background: "var(--btn-bg)", color: "var(--btn-text)", fontSize: 12, fontWeight: 600,
                textDecoration: "none", transition: "background 0.15s",
              }}
            >
              Apply for the Accelerator →
            </a>
          </div>
        )}
      </div>
    </div>
  );
}
